import type { CostActivityEntry, CostEntityType, CostSourceType } from '../types'
import { isPermissionError, isRecordGoneError } from './errors'
import { CHANGE_STATUS_KEY, CONTRACT_STATUS_KEY, INVOICE_STATUS_KEY } from './labels'
import { isZeroMoney } from './money'

type T = (key: string, opts?: Record<string, unknown>) => string

// ---- Budget drill-down (spec §10) ----

/** Where a budget drill-down entry opens (spec §10); null for the original budget itself. Expenses open in the ledger's sheet (ruling Q9). */
export function recordPath(projectId: string, sourceType: CostSourceType, id: string): string | null {
  const base = `/project/${projectId}/cost-control`
  switch (sourceType) {
    case 'CONTRACT': return `${base}/contracts/${id}`
    case 'CHANGE': return `${base}/changes/${id}`
    case 'INVOICE': return `${base}/invoices/${id}`
    case 'EXPENSE': return `${base}?tab=invoices&expense=${encodeURIComponent(id)}`
    default: return null
  }
}

// ---- Detail pages ----

export type RecordDetailState = 'loading' | 'ready' | 'notFound' | 'error'

/**
 * What a record's detail page shows (3a's contractDetailState, for every record type). `message` is the latest
 * refusal of the record read: the page's own query, or a reload after a write, which Apollo 2.6 does not hand to the
 * page's query. For someone who holds VIEW on the route's project a denial can only mean the record is gone or
 * belongs to another project; until the rights have loaded it is not told apart from "no permission". A record of
 * another project than the route's is not found either.
 */
export function recordDetailState(s: {
  loading: boolean
  hasData: boolean
  recordProjectId: string | null
  projectId: string
  message: string | null
  rightsLoading: boolean
  canView: boolean
  notFoundKey: string
}): RecordDetailState {
  const gone = isRecordGoneError(s.message, s.notFoundKey)
  if (s.hasData && !gone) return s.recordProjectId === s.projectId ? 'ready' : 'notFound'
  if (!s.hasData && s.loading) return 'loading'
  if (isPermissionError(s.message)) return s.rightsLoading ? 'loading' : s.canView ? 'notFound' : 'error'
  return gone || !s.message ? 'notFound' : 'error'
}

// ---- History (ruling Q13) ----

export const HISTORY_PAGE = 50

/** One page of one record's history. Skip 0 is the live page that writes reload with these exact variables. */
export const historyVariables = (projectId: string, entityType: CostEntityType, entityId: string, skip = 0) =>
  ({ projectId, entityType, entityId, take: HISTORY_PAGE, skip })

/** One page of the project history (1b-C spec §5); `entityTypes` undefined = every type. */
export const projectHistoryVariables = (projectId: string, entityTypes: string[] | undefined, skip = 0) =>
  ({ projectId, entityTypes, take: HISTORY_PAGE, skip })

const ENTITY_LABEL_KEY: Record<string, string> = {
  NODE: 'CostControl_entity_NODE', CONTRACT: 'CostControl_entity_CONTRACT', CHANGE: 'CostControl_entity_CHANGE', APPLICATION: 'CostControl_entity_APPLICATION',
  INVOICE: 'CostControl_entity_INVOICE', EXPENSE: 'CostControl_entity_EXPENSE', IMPORT: 'CostControl_entity_IMPORT', SETTINGS: 'CostControl_entity_SETTINGS',
  RFQ: 'CostControl_entity_RFQ',
}
export const entityLabelKey = (type: string): string | null => ENTITY_LABEL_KEY[type] ?? null

/** Where a project-history entry opens (1b-C spec §5): its record, the budget tab, or nowhere once the record itself is deleted. */
export function activityPath(projectId: string, e: Pick<CostActivityEntry, 'entityType' | 'entityId' | 'action'>): string | null {
  if (e.action === `${e.entityType}_DELETED`) return null
  switch (e.entityType) {
    case 'NODE':
    case 'IMPORT':
      return `/project/${projectId}/cost-control?tab=budget`
    case 'APPLICATION':
      return e.entityId ? `/project/${projectId}/cost-control/applications/${e.entityId}` : null
    case 'CONTRACT':
    case 'CHANGE':
    case 'INVOICE':
    case 'EXPENSE':
      return e.entityId ? recordPath(projectId, e.entityType as CostSourceType, e.entityId) : null
    case 'RFQ':
      return e.entityId ? `/project/${projectId}/cost-control/rfqs/${e.entityId}` : null
    default:
      return null
  }
}

/** The live first page, then older pages in order, without duplicates (an entry written meanwhile shifts the pages). */
export function mergeHistory(first: CostActivityEntry[], older: CostActivityEntry[][]): CostActivityEntry[] {
  const seen = new Set<string>()
  const out: CostActivityEntry[] = []
  ;[first, ...older].forEach((page) => page.forEach((e) => {
    if (!seen.has(e.id)) {
      seen.add(e.id)
      out.push(e)
    }
  }))
  return out
}

/** Older entries may exist when the last page loaded came back full. */
export const hasMoreHistory = (lastPageSize: number): boolean => lastPageSize >= HISTORY_PAGE

/**
 * M1: once older pages are loaded, a write reloads only the live first page (skip 0); the older pages, fetched by
 * `skip`, stay where they were and are no longer flush against it, so a write can push an entry into the gap between
 * them. The first page's own first entry id is the anchor: once it moves, `older` is stale and must be dropped so
 * the next "older" fetch starts over, contiguous with the new first page. `null` on either side (no data yet, or the
 * record just changed) is never treated as a move.
 */
export function reconcileOlderPages<E extends { id: string }>(prevFirstId: string | null, nextFirstId: string | null, older: E[][]): E[][] {
  if (prevFirstId === null || nextFirstId === null || prevFirstId === nextFirstId) return older
  return []
}

const ACTIVITY_KEY: Record<string, string> = {
  CONTRACT_CREATED: 'CostControl_activity_CONTRACT_CREATED',
  CONTRACT_UPDATED: 'CostControl_activity_CONTRACT_UPDATED',
  CONTRACT_LINES_SET: 'CostControl_activity_CONTRACT_LINES_SET',
  CONTRACT_ITEMS_SET: 'CostControl_activity_CONTRACT_ITEMS_SET',
  CONTRACT_ITEMS_IMPORTED: 'CostControl_activity_CONTRACT_ITEMS_IMPORTED',
  CONTRACT_STATUS_CHANGED: 'CostControl_activity_CONTRACT_STATUS_CHANGED',
  CONTRACT_DELETED: 'CostControl_activity_CONTRACT_DELETED',
  CONTRACT_REPRESENTATIVE_ADDED: 'CostControl_activity_CONTRACT_REPRESENTATIVE_ADDED',
  CONTRACT_REPRESENTATIVE_REMOVED: 'CostControl_activity_CONTRACT_REPRESENTATIVE_REMOVED',
  SUBMITTAL_LINKED: 'CostControl_activity_SUBMITTAL_LINKED',
  SUBMITTAL_UNLINKED: 'CostControl_activity_SUBMITTAL_UNLINKED',
  TENDER_LINKED: 'CostControl_activity_TENDER_LINKED',
  TENDER_UNLINKED: 'CostControl_activity_TENDER_UNLINKED',
  ATTACHMENT_ADDED: 'CostControl_activity_ATTACHMENT_ADDED',
  ATTACHMENT_REMOVED: 'CostControl_activity_ATTACHMENT_REMOVED',
  ATTACHMENT_REVISION_SWITCHED: 'CostControl_activity_ATTACHMENT_REVISION_SWITCHED',
  CHANGE_CREATED: 'CostControl_activity_CHANGE_CREATED',
  CHANGE_UPDATED: 'CostControl_activity_CHANGE_UPDATED',
  CHANGE_LINES_SET: 'CostControl_activity_CHANGE_LINES_SET',
  CHANGE_SUBMITTED: 'CostControl_activity_CHANGE_SUBMITTED',
  CHANGE_APPROVED: 'CostControl_activity_CHANGE_APPROVED',
  CHANGE_REJECTED: 'CostControl_activity_CHANGE_REJECTED',
  CHANGE_REOPENED: 'CostControl_activity_CHANGE_REOPENED',
  CHANGE_UNAPPROVED: 'CostControl_activity_CHANGE_UNAPPROVED',
  CHANGE_DELETED: 'CostControl_activity_CHANGE_DELETED',
  CHANGE_DISMISSED: 'CostControl_activity_CHANGE_DISMISSED',
  INVOICE_CREATED: 'CostControl_activity_INVOICE_CREATED',
  INVOICE_UPDATED: 'CostControl_activity_INVOICE_UPDATED',
  INVOICE_LINES_SET: 'CostControl_activity_INVOICE_LINES_SET',
  INVOICE_STATUS_CHANGED: 'CostControl_activity_INVOICE_STATUS_CHANGED',
  INVOICE_DELETED: 'CostControl_activity_INVOICE_DELETED',
  EXPENSE_CREATED: 'CostControl_activity_EXPENSE_CREATED',
  EXPENSE_UPDATED: 'CostControl_activity_EXPENSE_UPDATED',
  EXPENSE_DELETED: 'CostControl_activity_EXPENSE_DELETED',
  APPLICATION_CREATED: 'CostControl_activity_APPLICATION_CREATED',
  APPLICATION_UPDATED: 'CostControl_activity_APPLICATION_UPDATED',
  APPLICATION_LINES_SET: 'CostControl_activity_APPLICATION_LINES_SET',
  APPLICATION_SUBMITTED: 'CostControl_activity_APPLICATION_SUBMITTED',
  APPLICATION_RETURNED: 'CostControl_activity_APPLICATION_RETURNED',
  APPLICATION_REVIEW_SAVED: 'CostControl_activity_APPLICATION_REVIEW_SAVED',
  APPLICATION_CERTIFIED: 'CostControl_activity_APPLICATION_CERTIFIED',
  APPLICATION_UNCERTIFIED: 'CostControl_activity_APPLICATION_UNCERTIFIED',
  APPLICATION_INVOICE_RECORDED: 'CostControl_activity_APPLICATION_INVOICE_RECORDED',
  APPLICATION_INVOICE_CLEARED: 'CostControl_activity_APPLICATION_INVOICE_CLEARED',
  APPLICATION_DELETED: 'CostControl_activity_APPLICATION_DELETED',
  RETENTION_RELEASED: 'CostControl_activity_RETENTION_RELEASED',
  RETENTION_RELEASE_DELETED: 'CostControl_activity_RETENTION_RELEASE_DELETED',
  RFQ_CREATED: 'CostControl_activity_RFQ_CREATED',
  RFQ_UPDATED: 'CostControl_activity_RFQ_UPDATED',
  RFQ_DELETED: 'CostControl_activity_RFQ_DELETED',
  RFQ_QUOTE_ADDED: 'CostControl_activity_RFQ_QUOTE_ADDED',
  RFQ_QUOTE_UPDATED: 'CostControl_activity_RFQ_QUOTE_UPDATED',
  RFQ_QUOTE_DELETED: 'CostControl_activity_RFQ_QUOTE_DELETED',
  RFQ_AWARDED: 'CostControl_activity_RFQ_AWARDED',
  RFQ_AWARD_REVOKED: 'CostControl_activity_RFQ_AWARD_REVOKED',
  RFQ_CANCELLED: 'CostControl_activity_RFQ_CANCELLED',
  RFQ_REOPENED: 'CostControl_activity_RFQ_REOPENED',
  NODE_CREATED: 'CostControl_activity_NODE_CREATED',
  NODE_UPDATED: 'CostControl_activity_NODE_UPDATED',
  NODE_MOVED: 'CostControl_activity_NODE_MOVED',
  NODE_DELETED: 'CostControl_activity_NODE_DELETED',
  ESTIMATE_SET: 'CostControl_activity_estimate_set',
  BUDGET_IMPORTED: 'CostControl_activity_BUDGET_IMPORTED',
  BUDGET_LOCKED: 'CostControl_activity_BUDGET_LOCKED',
  BUDGET_UNLOCKED: 'CostControl_activity_BUDGET_UNLOCKED',
  SETTINGS_UPDATED: 'CostControl_activity_SETTINGS_UPDATED',
}
const CONTRACT_FIELD_KEY: Record<string, string> = {
  number: 'CostControl_contract_field_number',
  name: 'CostControl_contract_field_name',
  counterpartyName: 'CostControl_contract_field_counterparty',
  counterpartyIco: 'CostControl_contract_field_ico',
  appointedBy: 'CostControl_contract_field_appointedBy',
  contractType: 'CostControl_contract_field_type',
  mainValue: 'CostControl_contract_field_mainValue',
  signedAt: 'CostControl_contract_field_signedAt',
  startDate: 'CostControl_contract_field_startDate',
  endDate: 'CostControl_contract_field_endDate',
  notes: 'CostControl_contract_field_notes',
  retentionPercent: 'CostControl_contract_field_retentionPercent',
}
const FIELD_KEY: Record<string, Record<string, string>> = {
  CONTRACT: CONTRACT_FIELD_KEY,
  CHANGE: { title: 'CostControl_change_field_title', reason: 'CostControl_change_field_reason', contractId: 'CostControl_change_field_contract', cause: 'CostControl_change_field_cause' },
  INVOICE: { number: 'CostControl_invoice_field_number', issueDate: 'CostControl_invoice_field_issueDate', dueDate: 'CostControl_invoice_field_dueDate', note: 'CostControl_invoice_field_note', retentionPercent: 'CostControl_invoice_field_retentionPercent' },
  APPLICATION: { periodFrom: 'CostControl_application_field_periodFrom', periodTo: 'CostControl_application_field_periodTo', note: 'CostControl_application_field_note' },
  EXPENSE: {
    nodeId: 'CostControl_expense_field_item', description: 'CostControl_expense_field_description', supplierName: 'CostControl_expense_field_supplier',
    date: 'CostControl_expense_field_date', amount: 'CostControl_expense_field_amount',
  },
  NODE: {
    name: 'CostControl_col_name', code: 'CostControl_col_code', originalBudget: 'CostControl_col_originalBudget',
    forecastOverride: 'CostControl_col_projectedFinalCost', estimate: 'CostControl_col_estimate', buildingId: 'CostControl_link_building',
  },
  RFQ: { title: 'CostControl_rfq_field_title', description: 'CostControl_rfq_field_description', dueDate: 'CostControl_rfq_field_dueDate', nodeId: 'CostControl_rfq_field_node' },
  SETTINGS: { currency: 'CostControl_currency', alertThresholdPercent: 'CostControl_alert_threshold' },
}
const STATUS_KEY: Record<string, Record<string, string>> = { CONTRACT: CONTRACT_STATUS_KEY, CHANGE: CHANGE_STATUS_KEY, INVOICE: INVOICE_STATUS_KEY }
const label = (map: Record<string, string> | undefined, value: string, t: T) => (map && map[value] ? t(map[value]) : value)

const NODE_MONEY_FIELDS = ['originalBudget', 'forecastOverride', 'estimate']
const CONVERTED_KEY: Record<string, string> = {
  BUDGET_CHANGE: 'CostControl_activity_CHANGE_CONVERTED_BUDGET_CHANGE',
  CONTRACT_CHANGE: 'CostControl_activity_CHANGE_CONVERTED_CONTRACT_CHANGE',
}
const RFQ_QUOTE_FIELD_KEY: Record<string, string> = {
  supplierName: 'CostControl_rfq_quote_supplier', supplierIco: 'CostControl_contract_field_ico', amount: 'CostControl_rfq_quote_amount',
  receivedAt: 'CostControl_rfq_quote_receivedAt', note: 'CostControl_rfq_quote_note',
}
/** "Fasády s.r.o. · 380,50 Kč": a quote's supplier and its amount when it has one. */
const supplierAmount = (m: any, money: (v: string) => string): string | null => [m.supplierName, m.amount ? money(m.amount) : null].filter(Boolean).join(' · ') || null

/** One history entry → a title and an optional detail line, for every record type. `money` formats a decimal string. */
export function describeActivity(e: Pick<CostActivityEntry, 'action' | 'meta'> & { entityType?: string | null }, t: T, money: (v: string) => string): { title: string; detail: string | null } {
  const title = ACTIVITY_KEY[e.action] ? t(ACTIVITY_KEY[e.action], e.action === 'APPLICATION_INVOICE_RECORDED' ? { number: (e.meta ?? {}).number } : undefined) : e.action
  const m = e.meta ?? {}
  const entity = e.entityType ?? 'CONTRACT'
  switch (e.action) {
    case 'CONTRACT_UPDATED':
    case 'CHANGE_UPDATED':
    case 'INVOICE_UPDATED':
    case 'EXPENSE_UPDATED':
    case 'APPLICATION_UPDATED':
    case 'RFQ_UPDATED':
      return { title, detail: (m.changes ?? []).map((c: { field: string }) => label(FIELD_KEY[entity] ?? CONTRACT_FIELD_KEY, c.field, t)).join(', ') || null }
    case 'CONTRACT_STATUS_CHANGED':
    case 'INVOICE_STATUS_CHANGED':
      return { title, detail: `${label(STATUS_KEY[entity], m.from, t)} → ${label(STATUS_KEY[entity], m.to, t)}` }
    case 'CONTRACT_ITEMS_SET':
    case 'CONTRACT_LINES_SET':
    case 'CHANGE_LINES_SET':
    case 'INVOICE_LINES_SET':
    case 'APPLICATION_LINES_SET':
      return { title, detail: `${money(m.totalBefore ?? '0.00')} → ${money(m.totalAfter ?? '0.00')}` }
    case 'CONTRACT_ITEMS_IMPORTED':
      return { title, detail: t('CostControl_activity_items_imported', { count: m.count, amount: money(m.total ?? '0.00') }) }
    case 'CHANGE_SUBMITTED':
    case 'CHANGE_APPROVED':
      return { title, detail: m.total ? t('CostControl_activity_total', { amount: money(m.total) }) : null }
    case 'CHANGE_REJECTED':
      return { title, detail: m.note ? t('CostControl_activity_note', { note: m.note }) : null }
    case 'INVOICE_CREATED':
    case 'INVOICE_DELETED':
    case 'EXPENSE_CREATED':
    case 'EXPENSE_DELETED':
      return { title, detail: m.amount ? money(m.amount) : null }
    case 'ATTACHMENT_ADDED':
    case 'ATTACHMENT_REMOVED':
      return { title, detail: m.name ?? null }
    case 'ATTACHMENT_REVISION_SWITCHED':
      return { title, detail: `${m.name ?? ''} · v${m.fromVersion} → v${m.toVersion}` }
    case 'CONTRACT_CREATED':
      return {
        title,
        detail: m.awardedAmount ? t('CostControl_activity_from_tender', { amount: money(m.awardedAmount) })
          : m.rfqNumber ? t('CostControl_activity_from_rfq', { number: m.rfqNumber }) : null,
      }
    case 'CHANGE_CREATED':
      return {
        title: m.kind === 'POTENTIAL' ? t('CostControl_activity_CHANGE_CREATED_POTENTIAL') : title,
        detail: m.rfqNumber ? t('CostControl_activity_from_rfq', { number: m.rfqNumber }) : null,
      }
    // Potential changes spec §7.
    case 'CHANGE_CONVERTED':
      return { title: CONVERTED_KEY[m.toKind] ? t(CONVERTED_KEY[m.toKind]) : title, detail: m.total ? t('CostControl_activity_total', { amount: money(m.total) }) : null }
    case 'CHANGE_DISMISSED':
      return { title, detail: m.note ?? null }
    case 'CHANGE_RFQ_CREATED':
      return { title: t('CostControl_activity_CHANGE_RFQ_CREATED', { number: m.rfqNumber }), detail: null }
    case 'RFQ_CREATED':
      return { title, detail: m.sourceChangeNumber ? t('CostControl_rfq_from_potential', { number: m.sourceChangeNumber }) : null }
    case 'RFQ_QUOTE_ADDED':
    case 'RFQ_QUOTE_DELETED':
    case 'RFQ_AWARDED':
      return { title, detail: supplierAmount(m, money) }
    case 'RFQ_QUOTE_UPDATED': {
      const fields = (m.changes ?? []).map((c: { field: string; old: string | null; new: string | null }) => {
        const name = label(RFQ_QUOTE_FIELD_KEY, c.field, t)
        if (c.field !== 'amount') return name
        const amount = (v: string | null) => (v === null ? '—' : money(v))
        return `${name} ${amount(c.old)} → ${amount(c.new)}`
      }).join(', ')
      return { title, detail: [m.supplierName, fields].filter(Boolean).join(': ') || null }
    }
    case 'RFQ_AWARD_REVOKED':
      return { title, detail: m.supplierName ?? null }
    case 'APPLICATION_SUBMITTED':
    case 'APPLICATION_DELETED':
      return { title, detail: m.requested ? t('CostControl_activity_total', { amount: money(m.requested) }) : null }
    case 'APPLICATION_CERTIFIED':
    case 'APPLICATION_REVIEW_SAVED':
      return { title, detail: m.certified ? t('CostControl_activity_total', { amount: money(m.certified) }) : null }
    case 'APPLICATION_RETURNED':
      return { title, detail: m.note ? t('CostControl_activity_note', { note: m.note }) : null }
    case 'RETENTION_RELEASED':
    case 'RETENTION_RELEASE_DELETED':
      return { title, detail: m.amount ? money(m.amount) : null }
    case 'SUBMITTAL_LINKED':
    case 'SUBMITTAL_UNLINKED':
      return { title, detail: [m.displayNumber, m.title].filter(Boolean).join(' ') || null }
    case 'TENDER_LINKED':
    case 'TENDER_UNLINKED':
      return { title, detail: m.tenderName ?? null }
    case 'CONTRACT_REPRESENTATIVE_ADDED':
    case 'CONTRACT_REPRESENTATIVE_REMOVED':
      return { title, detail: m.userName ?? null }
    case 'NODE_UPDATED':
      return {
        title,
        detail: (m.changes ?? []).map((c: { field: string; old: string | null; new: string | null }) => {
          const name = label(FIELD_KEY.NODE, c.field, t)
          if (NODE_MONEY_FIELDS.indexOf(c.field) < 0) return name
          const amount = (v: string | null) => (v === null ? '—' : money(v))
          return `${name}: ${amount(c.old)} → ${amount(c.new)}`
        }).join(', ') || null,
      }
    case 'NODE_CREATED':
      return { title, detail: m.originalBudget && !isZeroMoney(m.originalBudget) ? t('CostControl_activity_node_budget', { amount: money(m.originalBudget) }) : null }
    case 'ESTIMATE_SET':
      return {
        title,
        detail: [m.amount ? money(m.amount) : t('CostControl_estimate_clear'), typeof m.itemCount === 'number' ? t('CostControl_estimate_group_items', { count: m.itemCount }) : null].filter(Boolean).join(' · '),
      }
    case 'NODE_DELETED':
      return { title, detail: typeof m.count === 'number' ? t('CostControl_activity_node_deleted', { count: m.count }) : null }
    case 'BUDGET_IMPORTED':
      return { title, detail: t('CostControl_activity_budget_imported', { created: m.itemsCreated ?? 0, updated: m.itemsUpdated ?? 0 }) }
    case 'SETTINGS_UPDATED':
      return {
        title,
        detail: (m.changes ?? []).map((c: { field: string; old: unknown; new: unknown }) => {
          const show = (v: unknown) => (v === null || v === undefined ? '—' : c.field === 'alertThresholdPercent' ? `${v} %` : String(v))
          return `${label(FIELD_KEY.SETTINGS, c.field, t)}: ${show(c.old)} → ${show(c.new)}`
        }).join(', ') || null,
      }
    default:
      return { title, detail: null }
  }
}
